<script setup>
/**
 * The wall, drawn. What it SAYS is `paywallView` in src/billing.js; this is
 * only the shape, so every state can be rendered in a test without a phone.
 *
 * ONE SCREEN, NO SCROLL. Top to bottom: the close button, one small picture
 * (the app's own vehicle rolling toward a stop, `PaywallHero`), the headline
 * and one line under it, three benefit rows, the two plans side by side, the one
 * button, and the small print. It is laid out to fit a 393x852 iPhone and,
 * with the compact rules at the bottom of the style block, a 375x667 iPhone SE,
 * without the body scrolling. The button, the renewal terms and the three links
 * are pinned at the foot so the price and the terms are never away from the
 * button that charges it (App Store guideline 3.1.2, Play's subscription policy).
 *
 * CHOOSING IS NOT BUYING. The plan cards are radio buttons; the only thing that
 * starts a purchase is the pinned button, which names what it will do.
 */
import PaywallHero from './PaywallHero.vue';
import PaywallProof from './PaywallProof.vue';

defineProps({
  view: { type: Object, required: true },
});
const emit = defineEmits(['buy', 'select', 'restore', 'retry', 'close', 'link']);
</script>

<template>
  <section class="wall" aria-labelledby="wall-title">
    <header class="wall-head">
      <p class="wall-stamp">{{ view.stamp }}</p>
      <button type="button" class="wall-close" aria-label="Not now" @click="emit('close')">
        <span aria-hidden="true">✕</span>
      </button>
    </header>

    <div class="wall-body">
      <PaywallHero class="wall-hero" :livery="view.hero?.livery || ''" :rail="view.hero?.rail !== false" />
      <PaywallProof v-if="view.hero?.rail !== false" class="wall-proof" />

      <h1 id="wall-title" class="wall-title">{{ view.title }}</h1>
      <p v-if="view.why" class="wall-why">{{ view.why }}</p>

      <ul class="wall-benefits" role="list">
        <li v-for="b in view.benefits" :key="b.key" class="wall-benefit">
          <span class="wall-glyph" aria-hidden="true">
            <svg v-if="b.icon === 'follow'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="7" y="2.5" width="10" height="19" rx="3" /><path d="M10.5 6h3" /><path d="M9.5 13.5h5M9.5 16.5h3" /></svg>
            <svg v-else-if="b.icon === 'alarm'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="7" /><path d="M12 9.5V13l2.5 1.5" /><path d="M4 5.5 7 3M20 5.5 17 3" /></svg>
            <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9Z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>
          </span>
          <span class="wall-benefit-text">
            {{ b.text }}
            <span v-if="b.note" class="wall-benefit-note">{{ b.note }}</span>
          </span>
        </li>
      </ul>

      <div v-if="view.plans.length" class="wall-plans" role="radiogroup" aria-label="Plans">
        <button
          v-for="plan in view.plans"
          :key="plan.id"
          type="button"
          role="radio"
          class="wall-plan"
          :class="{ 'wall-plan-primary': plan.primary, 'wall-plan-on': plan.selected }"
          :aria-checked="plan.selected ? 'true' : 'false'"
          :disabled="plan.disabled"
          :data-plan="plan.id"
          @click="emit('select', plan.id)"
        >
          <span v-if="plan.chip" class="wall-chip">{{ plan.chip }}</span>
          <span class="wall-plan-top">
            <span class="wall-radio" aria-hidden="true" />
            <span class="wall-plan-label">{{ plan.label }}</span>
          </span>
          <span class="wall-plan-price"><span class="wall-amount">{{ plan.amount }}</span><span class="wall-per">/{{ plan.period }}</span></span>
          <span class="wall-plan-note">{{ plan.note }}</span>
        </button>
      </div>
    </div>

    <footer class="wall-foot">
      <p v-if="view.status" class="wall-status" role="status" aria-live="polite">{{ view.status }}</p>
      <div v-if="view.retry" class="wall-retry">
        <button type="button" class="la-button" @click="emit('retry')">Try again</button>
      </div>

      <template v-if="view.cta">
        <button
          type="button"
          class="wall-cta"
          :disabled="view.cta.disabled"
          :data-buy="view.cta.planId"
          @click="emit('buy', view.cta.planId)"
        >
          {{ view.cta.label }}
        </button>
        <p class="wall-cta-sub">{{ view.cta.sub }}</p>
      </template>

      <button v-if="view.dismiss" type="button" class="wall-cta" @click="emit('close')">{{ view.dismiss.label }}</button>

      <p v-if="view.terms" class="wall-terms">{{ view.terms }}</p>

      <nav class="wall-links" aria-label="Subscription links">
        <button type="button" class="wall-link" :disabled="view.busy" @click="emit('restore')">
          {{ view.restoreLabel }}
        </button>
        <span class="wall-dot" aria-hidden="true">·</span>
        <a
          class="wall-link" :href="view.termsUrl" target="_blank" rel="noopener noreferrer"
          @click.prevent="emit('link', view.termsUrl)"
        >{{ view.termsLabel }}</a>
        <span class="wall-dot" aria-hidden="true">·</span>
        <a
          class="wall-link" :href="view.privacyUrl" target="_blank" rel="noopener noreferrer"
          @click.prevent="emit('link', view.privacyUrl)"
        >{{ view.privacyLabel }}</a>
      </nav>
    </footer>
  </section>
</template>

<style scoped>
.wall {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--la-carriage);
  color: var(--la-enamel);
  font-family: var(--la-font-body);
}

.wall-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: calc(env(safe-area-inset-top, 0px) + 6px) 8px 0 20px;
  min-height: var(--la-control-min);
}

.wall-stamp {
  margin: 0;
  font-family: var(--la-font-mono);
  font-size: 11px;
  letter-spacing: var(--la-control-track);
  color: var(--la-dim);
}

.wall-close {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--la-control-min);
  height: var(--la-control-min);
  padding: 0;
  background: transparent;
  color: var(--la-enamel);
  border: 0;
  border-radius: 50%;
  font-size: 17px;
  cursor: pointer;
}
.wall-close:focus-visible { outline: 2px solid var(--la-enamel); outline-offset: -4px; }

/* The body holds still. overflow is a safety net for a very large text size,
   not a layout: at the sizes the wall is designed for it never engages. */
.wall-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 20px 12px;
  display: flex;
  flex-direction: column;
}

.wall-hero { margin-top: 2px; }

.wall-title {
  margin: 8px 0 0;
  font-family: var(--la-font-display);
  font-stretch: 125%;
  font-size: 26px;
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.wall-why {
  margin: 6px 0 0;
  font-size: 15px;
  line-height: 1.4;
  color: var(--la-dim);
}

.wall-benefits {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.wall-benefit {
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 12px;
  align-items: center;
}

.wall-glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--la-platform);
  border: 0.5px solid var(--la-rule);
  color: var(--la-enamel);
}

.wall-benefit-text {
  font-size: 15px;
  line-height: 1.3;
  text-wrap: balance;
}

.wall-benefit-note {
  display: block;
  margin-top: 1px;
  font-size: 13px;
  color: var(--la-dim);
}

.wall-plans {
  margin-top: auto;
  padding-top: 18px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.wall-plan {
  position: relative;
  box-sizing: border-box;
  min-width: 0;
  padding: 14px 12px 12px;
  display: grid;
  align-content: start;
  row-gap: 4px;
  text-align: left;
  background: transparent;
  color: var(--la-enamel);
  border: 1px solid var(--la-rule);
  border-radius: 12px;
  font-family: var(--la-font-body);
  cursor: pointer;
  transition: border-color var(--la-dur), background var(--la-dur);
}
.wall-plan:focus-visible { outline: 2px solid var(--la-enamel); outline-offset: 2px; }
.wall-plan:disabled { opacity: 0.55; cursor: default; }

.wall-plan-on {
  border-color: var(--la-enamel);
  box-shadow: inset 0 0 0 1px var(--la-enamel);
  background: var(--la-platform);
}

/* Straddles the card's top edge, so the card keeps its height. */
.wall-chip {
  position: absolute;
  top: -10px;
  left: 8px;
  padding: 0 7px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  border-radius: 10px;
  background: var(--la-enamel);
  color: var(--la-carriage);
  font-family: var(--la-font-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: -0.03em;
  white-space: nowrap;
}

.wall-plan-top { display: flex; align-items: center; gap: 8px; }

.wall-radio {
  flex: none;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 1.5px solid var(--la-dim);
  border-radius: 50%;
}
.wall-plan-on .wall-radio { border: 5.5px solid var(--la-enamel); }

.wall-plan-label {
  font-family: var(--la-font-display);
  font-stretch: 125%;
  font-size: 15px;
  font-weight: 500;
}

/* THE BILLED PRICE IS THE LOUDEST NUMBER on the card. */
.wall-plan-price { white-space: nowrap; font-variant-numeric: tabular-nums; }
.wall-amount {
  font-family: var(--la-font-display);
  font-stretch: 112%;
  font-size: 20px;
  font-weight: 600;
}
.wall-per { font-size: 14px; color: var(--la-dim); }

.wall-plan-note {
  min-height: 2.7em;
  font-size: 13px;
  line-height: 1.35;
  color: var(--la-dim);
}

.wall-foot {
  flex: none;
  padding: 0 20px calc(env(safe-area-inset-bottom, 0px) + 2px);
  background: var(--la-carriage);
}

.wall-status {
  margin: 0 0 8px;
  font-family: var(--la-font-mono);
  font-size: 12px;
  line-height: 1.4;
  color: var(--la-dim);
}

.wall-retry { margin: 0 0 8px; }

.wall-cta {
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  padding: 0 16px;
  background: var(--la-enamel);
  color: var(--la-carriage);
  border: 0;
  border-radius: 12px;
  font-family: var(--la-font-display);
  font-stretch: 125%;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
}
.wall-cta:focus-visible { outline: 2px solid var(--la-enamel); outline-offset: 3px; }
.wall-cta:disabled { opacity: 0.5; cursor: default; }

.wall-cta-sub {
  margin: 6px 0 0;
  text-align: center;
  font-size: 13px;
  color: var(--la-dim);
}

.wall-terms {
  margin: 8px 0 0;
  font-size: 11px;
  line-height: 1.4;
  color: var(--la-dim);
}

.wall-links {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 2px;
}

.wall-link {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 8px;
  background: transparent;
  color: var(--la-enamel);
  border: 0;
  font-family: var(--la-font-mono);
  font-size: 11px;
  letter-spacing: var(--la-control-track);
  text-decoration: none;
  cursor: pointer;
}
.wall-link:disabled { color: var(--la-dim); cursor: default; }
.wall-link:focus-visible { outline: 2px solid var(--la-enamel); outline-offset: -6px; }

.wall-dot {
  color: var(--la-dim);
  font-size: 11px;
}

/* COMPACT: an iPhone SE (375x667) and anything else short. Same content, tighter
   rhythm; nothing is dropped, and the wall still does not scroll. */
@media (max-height: 740px) {
  .wall-head { min-height: 40px; padding-top: calc(env(safe-area-inset-top, 0px) + 2px); }
  .wall-close { width: 40px; height: 40px; }
  .wall-title { margin-top: 6px; font-size: 22px; }
  .wall-why { margin-top: 4px; font-size: 14px; }
  /* The picture and the three rows say what the line did; on a short screen the price needs the room. */
  .wall-why { display: none; }
  .wall-benefits { margin-top: 6px; gap: 4px; }
  .wall-benefit { grid-template-columns: 28px 1fr; gap: 10px; }
  .wall-glyph { width: 28px; height: 28px; }
  .wall-benefit-text { font-size: 14px; line-height: 1.25; }
  .wall-plans { padding-top: 12px; }
  .wall-plan { padding: 12px 10px 8px; row-gap: 2px; }
  .wall-amount { font-size: 18px; }
  .wall-plan-note { font-size: 12px; min-height: 1.4em; }
  .wall-cta { min-height: 48px; }
  .wall-cta-sub { margin-top: 4px; font-size: 12px; }
  .wall-terms { margin-top: 4px; font-size: 11px; line-height: 1.3; }
  .wall-link { min-height: 34px; }
}

@media (prefers-reduced-motion: reduce) {
  .wall-plan { transition: none; }
}
</style>
